import React from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useAbsoluteUrl } from 'hooks/useAbsoluteUrl/useAbsoluteUrl';
import { BreadcrumbPath, SchemaItem } from './types';

export const useBreadcrumbListSchema = ({
    breadcrumbs = [],
}: {
    breadcrumbs: BreadcrumbPath[];
}) => {
    const getAbsoluteUrl = useAbsoluteUrl();

    const schemaItems = breadcrumbs
        .filter(
            (crumb): crumb is SchemaItem =>
                !!crumb.label &&
                crumb.path !== '' &&
                !crumb.path.startsWith('/browse'),
        )
        .map((schemaItem) => ({
            label: schemaItem.label,
            link: getAbsoluteUrl(schemaItem.path),
            isCurrent: schemaItem.isCurrent,
        }));

    if (!schemaItems || !schemaItems?.length) return null;

    const breadcrumbListSchema = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: schemaItems.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.label,
            ...(!item.isCurrent && {
                item: item.link,
            }),
        })),
    };

    return breadcrumbListSchema;
};

export const BreadcrumbListSchema = ({
    breadcrumbs,
}: {
    breadcrumbs: BreadcrumbPath[];
}) => {
    const breadcrumbListSchema = useBreadcrumbListSchema({ breadcrumbs });

    if (!breadcrumbListSchema) return null;

    return (
        <HelmetProvider>
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(breadcrumbListSchema)}
                </script>
            </Helmet>
        </HelmetProvider>
    );
};
